import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';

import * as Constants from '../../constants';
import { useFlags } from '../../contexts/LDContext';
import { FilterSection } from '../../shared/components/FilterDrawer';
import type { RidesQueryParams } from '../types';

import { LOCATIONS_PER_PAGE, useRidesLocations } from './useRidesLocations';
import { useRidesServices } from './useRidesServices';
import { TAGS_PER_PAGE, useRidesTags } from './useRidesTags';

interface RideFiltersSource {
  queryParams: RidesQueryParams;
  setActiveFilters?: (selections: Record<string, string[]>) => void;
  clearAllFilters?: () => void;
}

export const useRideFilters = (
  { queryParams, setActiveFilters, clearAllFilters }: RideFiltersSource,
  isDrawerOpen = false
) => {
  const { sharedBillingRolesFilterEnabled } = useFlags();
  const [locationsSearchQuery, setLocationsSearchQuery] = useState('');
  const [tagsSearchQuery, setTagsSearchQuery] = useState('');

  const {
    locations,
    baselineTotalCount: locationsBaselineTotalCount,
    isFetching: isFetchingLocations,
    hasMore: locationsHasMore,
    fetchNextPage: fetchMoreLocations,
  } = useRidesLocations({ enabled: isDrawerOpen, query: locationsSearchQuery });

  const {
    tags,
    baselineTotalCount: tagsBaselineTotalCount,
    isFetching: isFetchingTags,
    hasMore: tagsHasMore,
    fetchNextPage: fetchMoreTags,
  } = useRidesTags({ enabled: isDrawerOpen, query: tagsSearchQuery });

  const { services, isFetching: isFetchingServices } = useRidesServices({ enabled: isDrawerOpen });

  const debouncedSetLocationsSearchQuery = useMemo(() => debounce(setLocationsSearchQuery, 300), []);
  const debouncedSetTagsSearchQuery = useMemo(() => debounce(setTagsSearchQuery, 300), []);

  useEffect(() => () => debouncedSetLocationsSearchQuery.cancel(), [debouncedSetLocationsSearchQuery]);
  useEffect(() => () => debouncedSetTagsSearchQuery.cancel(), [debouncedSetTagsSearchQuery]);

  // Drawer just closed — reset search so reopening shows the unfiltered list, not stale search results
  useEffect(() => {
    if (!isDrawerOpen) {
      setLocationsSearchQuery('');
      setTagsSearchQuery('');
    }
  }, [isDrawerOpen]);

  const handleLocationsSearch = useCallback(
    (query: string) => {
      if (query && query.length < Constants.MINIMUM_SEARCH_STRING_LENGTH) {
        return;
      }
      debouncedSetLocationsSearchQuery(query);
    },
    [debouncedSetLocationsSearchQuery]
  );

  const handleTagsSearch = useCallback(
    (query: string) => {
      if (query && query.length < Constants.MINIMUM_SEARCH_STRING_LENGTH) {
        return;
      }
      debouncedSetTagsSearchQuery(query);
    },
    [debouncedSetTagsSearchQuery]
  );

  const handleLocationsLoadMore = useCallback(() => {
    fetchMoreLocations();
  }, [fetchMoreLocations]);

  const handleTagsLoadMore = useCallback(() => {
    fetchMoreTags();
  }, [fetchMoreTags]);

  const locationOptions = locations.map((location) => ({ label: location.name, value: String(location.id) }));
  const tagOptions = tags.map((tag) => ({ label: tag, value: tag }));
  const serviceOptions = services.map((service) => ({ label: service.name, value: String(service.id) }));

  const sections: FilterSection[] = [
    {
      key: 'accountLocationIds',
      label: 'Location',
      options: locationOptions,
      selected: queryParams.accountLocationIds ?? [],
      searchable: locationsBaselineTotalCount > LOCATIONS_PER_PAGE,
      searchPlaceholder: 'Search locations',
      onSearch: handleLocationsSearch,
      isSearching: isFetchingLocations,
      hasMore: locationsHasMore,
      onLoadMore: handleLocationsLoadMore,
      isLoading: isFetchingLocations,
    },
    {
      key: 'tagNames',
      label: 'Tags',
      options: tagOptions,
      selected: queryParams.tagNames ?? [],
      searchable: tagsBaselineTotalCount > TAGS_PER_PAGE,
      searchPlaceholder: 'Search tags',
      onSearch: handleTagsSearch,
      isSearching: isFetchingTags,
      hasMore: tagsHasMore,
      onLoadMore: handleTagsLoadMore,
      isLoading: isFetchingTags,
    },
    {
      key: 'serviceIds',
      label: 'Services',
      options: serviceOptions,
      selected: queryParams.serviceIds ?? [],
      isLoading: isFetchingServices,
      emptyMessage: 'No services enabled',
    },
    ...(sharedBillingRolesFilterEnabled
      ? [
          {
            key: 'sharedBillingRoles',
            label: 'Shared Billing',
            options: [
              { label: 'Attending', value: 'attending' },
              { label: 'Residence', value: 'residence' },
              { label: 'No shared billing', value: 'none' },
            ],
            selected: queryParams.sharedBillingRoles ?? [],
          },
        ]
      : []),
  ];

  const activeFilterCount =
    (queryParams.accountLocationIds?.length ?? 0) +
    (queryParams.tagNames?.length ?? 0) +
    (queryParams.serviceIds?.length ?? 0) +
    (sharedBillingRolesFilterEnabled ? queryParams.sharedBillingRoles?.length ?? 0 : 0);

  return {
    sections,
    activeFilterCount,
    hasActiveFilters: activeFilterCount > 0,
    setActiveFilters: setActiveFilters ?? (() => {}),
    clearAllFilters: clearAllFilters ?? (() => {}),
  };
};
